<template>
  <div class="service-page">
    <ServicePageHeader
      icon="fas fa-clinic-medical"
      title="Health Services"
      subtitle="Comprehensive healthcare support for the campus community"
      show-back-link
    />

    <!-- Main Content -->
    <main class="service-content">
      <div class="container">
        <!-- Overview -->
        <section class="content-section">
          <h2>
            <i class="fas fa-info-circle section-icon" />
            Overview
          </h2>
          <p>
            The Campus Health Center provides accessible healthcare services to all enrolled students.
            Our medical team is committed to promoting wellness, preventing illness, and providing
            quality care for the campus community.
          </p>
        </section>

        <!-- Services Offered -->
        <section class="content-section">
          <h2>
            <i class="fas fa-stethoscope section-icon" />
            Medical Services
          </h2>
          <div class="services-grid">
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-user-md" />
              </div>
              <h3>General Consultation</h3>
              <p>Medical check-ups, diagnosis, and treatment for common illnesses.</p>
            </div>
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-first-aid" />
              </div>
              <h3>First Aid</h3>
              <p>Emergency first aid treatment and basic wound care.</p>
            </div>
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-syringe" />
              </div>
              <h3>Immunization</h3>
              <p>Vaccination services and immunization records management.</p>
            </div>
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-heartbeat" />
              </div>
              <h3>Vital Signs Check</h3>
              <p>Blood pressure, temperature, and other vital signs monitoring.</p>
            </div>
            <div class="service-card">
              <div class="card-icon green">
                <i class="fas fa-pills" />
              </div>
              <h3>Medicine Dispensary</h3>
              <p>Basic over-the-counter medications for common ailments.</p>
            </div>
            <div class="service-card">
              <div class="card-icon yellow">
                <i class="fas fa-file-medical" />
              </div>
              <h3>Medical Certificates</h3>
              <p>Issuance of medical certificates and health clearances.</p>
            </div>
          </div>
        </section>

        <!-- Mental Health -->
        <section class="content-section highlight">
          <div class="highlight-content">
            <div class="highlight-icon">
              <i class="fas fa-brain" />
            </div>
            <div class="highlight-text">
              <h3>Mental Health Support</h3>
              <p>
                We understand the importance of mental wellness. Our clinic offers referrals
                to professional counselors and mental health resources. If you're feeling
                overwhelmed, anxious, or stressed, please don't hesitate to reach out.
              </p>
              <router-link
                to="/services/counseling"
                class="highlight-link"
              >
                Visit Counseling Services
                <i class="fas fa-arrow-right" />
              </router-link>
            </div>
          </div>
        </section>

        <!-- Schedule -->
        <section class="content-section">
          <h2>
            <i class="fas fa-calendar-alt section-icon" />
            Clinic Hours
          </h2>
          <div class="schedule-card">
            <div class="schedule-row">
              <span class="day">Monday - Friday</span>
              <span class="time">7:30 AM - 5:30 PM</span>
            </div>
            <div class="schedule-row">
              <span class="day">Saturday</span>
              <span class="time">8:00 AM - 12:00 PM</span>
            </div>
            <div class="schedule-row">
              <span class="day">Sunday & Holidays</span>
              <span class="time">Emergency Only</span>
            </div>
          </div>
          <p class="schedule-note">
            <i class="fas fa-exclamation-circle" />
            For emergencies outside clinic hours, please proceed to the nearest hospital.
          </p>
        </section>

        <!-- Contact -->
        <section class="content-section">
          <h2>
            <i class="fas fa-phone section-icon" />
            Contact Information
          </h2>
          <div class="contact-info">
            <div class="contact-item">
              <i class="fas fa-map-marker-alt" />
              <span>Ground Floor, Student Services Building</span>
            </div>
            <div class="contact-item">
              <i class="fas fa-phone" />
              <span>(078) 123-4567 Local 100</span>
            </div>
            <div class="contact-item emergency">
              <i class="fas fa-ambulance" />
              <span>Emergency Hotline: (078) 123-4599</span>
            </div>
            <div class="contact-item">
              <i class="fas fa-envelope" />
              <span>clinic@nc.edu.ph</span>
            </div>
          </div>
        </section>

        <!-- CTA -->
        <section class="cta-section">
          <h2>Need Medical Assistance?</h2>
          <p>Schedule a consultation or walk in during clinic hours.</p>
          <button
            class="cta-button"
            @click="scheduleAppointment"
          >
            <i class="fas fa-calendar-plus" />
            Schedule Visit
          </button>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import ServicePageHeader from '@/components/landing/ServicePageHeader.vue'
import '@/assets/css/landing/pages/services.css'

export default {
  name: 'HealthService',

  components: {
    ServicePageHeader
  },

  metaInfo: () => ({
    title: 'Health Services'
  }),

  methods: {
    scheduleAppointment () {
      // TODO: Implement appointment scheduling
      this.$bvToast.toast('Appointment scheduling coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>

<style scoped>
.service-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header */
.service-header {
  position: relative;
  padding: 6rem 1.5rem 4rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-align: center;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
}

.bg-pattern {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.header-content {
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
}

.back-link {
  position: absolute;
  top: -3rem;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  transition: all 0.2s;
}

.back-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.header-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
}

.service-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
}

.service-header p {
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
}

/* Content */
.service-content {
  padding: 3rem 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.content-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.content-section h2 {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e3a2f;
  margin: 0 0 1rem;
}

.section-icon {
  color: #10b981;
}

.content-section p {
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 640px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 900px) {
  .services-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.service-card {
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.card-icon.green {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.card-icon.yellow {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
}

.service-card h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3a2f;
  margin: 0 0 0.5rem;
}

.service-card p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* Highlight Section */
.content-section.highlight {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(251, 191, 36, 0.05) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.highlight-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  text-align: center;
}

@media (min-width: 640px) {
  .highlight-content {
    flex-direction: row;
    text-align: left;
  }
}

.highlight-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.highlight-text h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e3a2f;
  margin: 0 0 0.5rem;
}

.highlight-text p {
  margin-bottom: 1rem;
}

.highlight-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  font-weight: 500;
  text-decoration: none;
}

.highlight-link:hover {
  text-decoration: underline;
}

/* Schedule */
.schedule-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.schedule-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.schedule-row:last-child {
  border-bottom: none;
}

.day {
  font-weight: 500;
  color: #1e3a2f;
}

.time {
  color: #10b981;
  font-weight: 500;
}

.schedule-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 8px;
  color: #d97706;
  font-size: 0.875rem;
}

/* Contact */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #64748b;
}

.contact-item i {
  width: 20px;
  color: #10b981;
}

.contact-item.emergency {
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  color: #dc2626;
  font-weight: 500;
}

.contact-item.emergency i {
  color: #dc2626;
}

/* CTA */
.cta-section {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: white;
}

.cta-section h2 {
  font-size: 1.5rem;
  margin: 0 0 0.5rem;
}

.cta-section p {
  opacity: 0.9;
  margin: 0 0 1.5rem;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: white;
  color: #059669;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}
</style>
